import { useCallback, useEffect, useRef, useState } from 'react';
import { EdgeApiError, fetchEdgeNodes } from './edgeApi';
import type { AuthHeadersFn, EdgeNodeList } from './edgeTypes';

export const EDGE_POLL_MS = 10_000;

export interface EdgeNodesState {
  /** Null whenever polling is off — liveness is never shown from a frozen copy. */
  data: EdgeNodeList | null;
  error: string | null;
  /** HTTP status of the last failure — 403 means the user has no active group. */
  errorStatus: number | null;
  refresh: () => Promise<void>;
}

/**
 * The single poll of GET /api/edge/nodes. App owns it so the map markers, the sidebar and
 * the panel share one request every EDGE_POLL_MS.
 *
 * - Disabled → interval cleared AND data dropped, so nothing renders stale liveness.
 * - Group change → data dropped and refetched at once.
 * - Responses older than the latest request are discarded (a slow interval fetch must not
 *   overwrite a refresh() issued after an action).
 * - A 403 ('no group') stops the interval until the group changes, polling is re-enabled,
 *   or a manual refresh() succeeds (the panel calls one on mount).
 */
export function useEdgeNodes(
  getAuthHeaders: AuthHeadersFn,
  enabled: boolean,
  groupId: number | null | undefined,
): EdgeNodesState {
  const [data, setData] = useState<EdgeNodeList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [noGroup, setNoGroup] = useState(false);
  const headersRef = useRef(getAuthHeaders);
  headersRef.current = getAuthHeaders;
  const seq = useRef(0);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const next = await fetchEdgeNodes(headersRef.current);
      if (!alive.current || mine !== seq.current) return;
      setData(next);
      setError(null);
      setErrorStatus(null);
      setNoGroup(false);
    } catch (e) {
      if (!alive.current || mine !== seq.current) return;
      const status = e instanceof EdgeApiError ? e.status : null;
      setError(e instanceof Error ? e.message : String(e));
      setErrorStatus(status);
      if (status === 403) {
        setData(null);
        setNoGroup(true);
      }
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // A new group or a fresh enable is a new question: forget the old answer and the 403.
  useEffect(() => {
    seq.current += 1; // discard anything still in flight for the previous group/state
    setData(null);
    setError(null);
    setErrorStatus(null);
    setNoGroup(false);
  }, [enabled, groupId]);

  useEffect(() => {
    if (!enabled || noGroup) return undefined;
    refresh();
    const id = window.setInterval(refresh, EDGE_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, noGroup, groupId, refresh]);

  return { data, error, errorStatus, refresh };
}
